import { Link, router, usePage } from "@inertiajs/react"
import { LogOut, Menu, Settings } from "lucide-react"
import { useRef, useState } from "react"

import { NotificationDropdown } from "@/components/notification-dropdown"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"
import { sessionPath, settingsProfilePath } from "@/routes"

interface AuthUser {
  id: number
  email: string
  name: string
  avatar?: string
}

interface AuthSession {
  id: number
}

export function MobileTopNav({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  const { auth } = usePage<{
    auth: { user: AuthUser; session: AuthSession }
  }>().props
  const { user, session } = auth
  const getInitials = useInitials()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleLogout = () => {
    setUserMenuOpen(false)
    router.flushAll()
  }

  return (
    <header className="bg-hpi-blue flex h-12 shrink-0 items-center px-3 text-white">
      {/* Hamburger */}
      <button
        onClick={onOpenDrawer}
        className="flex items-center justify-center rounded p-1.5 hover:bg-white/15"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Logo — centered */}
      <div className="flex flex-1 justify-center">
        <Link href="/" prefetch>
          <img src="/HPI-Logo-White.svg" alt="HawkPI" className="h-6" />
        </Link>
      </div>

      {/* Right icons */}
      <div className="flex items-center gap-2">
        <NotificationDropdown />

        {/* User avatar */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex cursor-pointer items-center"
            aria-label="User menu"
          >
            <Avatar className="h-7 w-7">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="bg-white/20 text-[10px] font-medium text-white">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
          </button>

          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className="border-hpi-light-gray absolute top-full right-0 z-50 mt-2 w-64 rounded-lg border bg-white shadow-lg">
                <div className="border-hpi-pale-gray flex items-center gap-3 border-b px-4 py-3">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback className="bg-hpi-light-blue text-hpi-dark-blue text-sm font-medium">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-hpi-dark-gray truncate text-sm font-medium">
                      {user.name}
                    </div>
                    <div className="text-hpi-med-gray truncate text-xs">
                      {user.email}
                    </div>
                  </div>
                </div>
                <div className="py-1">
                  <Link
                    href={settingsProfilePath()}
                    className="text-hpi-dark-gray hover:bg-hpi-pale-gray flex items-center gap-2 px-4 py-2 text-sm"
                    onClick={() => setUserMenuOpen(false)}
                    prefetch
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>
                  <Link
                    method="delete"
                    href={sessionPath({ id: session.id })}
                    as="button"
                    className="text-hpi-dark-gray hover:bg-hpi-pale-gray flex w-full items-center gap-2 px-4 py-2 text-sm"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                    Log out
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
